import { getLiveTelemetry, postTelemetryBatchQuery } from "@/lib/api/endpoints/telemetry";
import type { TelemetryBatchSeriesResultDto, TelemetryDto } from "@/lib/api/types";

import type { HmiCanvasDocument } from "../schema/hmi-canvas.schema";
import type { HmiBinding } from "../schema/hmi-bindings.schema";
import { buildTelemetryQueries } from "./buildTelemetryQueries";
import { compileRuntimeGraph, type CompiledHmiRuntimeGraph } from "./compileRuntimeGraph";
import type { HmiRuntimeError } from "./runtime-errors";
import { sourceQueryId } from "./sourceFingerprint";
import { HmiTelemetryCache } from "./telemetry-cache";

export type HmiRuntimeSnapshot = {
  graph: CompiledHmiRuntimeGraph;
  telemetry: Record<string, TelemetryBatchSeriesResultDto>;
  errors: HmiRuntimeError[];
};

type HmiRuntimeScope = {
  tenantId: string | null;
  allWorkspaces: boolean;
  dashboardId?: string | null;
};

type HmiLiveTelemetryBinding = HmiBinding & {
  source: Extract<HmiBinding["source"], { kind: "telemetry" }>;
};

export class HmiRuntimeEngine {
  private readonly cache = new HmiTelemetryCache();

  constructor(private readonly scope: HmiRuntimeScope) {}

  compile(document: HmiCanvasDocument): CompiledHmiRuntimeGraph {
    return compileRuntimeGraph(document);
  }

  private buildLiveSeries(rows: TelemetryDto[], channelKey: string): TelemetryBatchSeriesResultDto {
    const timestamps: string[] = [];
    const values: Array<number | null> = [];

    for (const row of rows) {
      const rawValue = row.data?.[channelKey];
      let numericValue: number | null = null;
      if (typeof rawValue === "number" && Number.isFinite(rawValue)) {
        numericValue = rawValue;
      } else if (typeof rawValue === "boolean") {
        numericValue = rawValue ? 1 : 0;
      } else if (typeof rawValue === "string") {
        const normalized = rawValue.trim().toLowerCase();
        if (["1", "true", "on", "open", "opened", "yes", "active", "running"].includes(normalized)) {
          numericValue = 1;
        } else if (["0", "false", "off", "close", "closed", "no", "inactive", "stopped"].includes(normalized)) {
          numericValue = 0;
        } else {
          const parsed = Number(rawValue);
          numericValue = Number.isFinite(parsed) ? parsed : null;
        }
      }
      timestamps.push(row.time);
      values.push(numericValue);
    }

    return {
      timestamps,
      values,
      bucket_seconds: 5,
      error: null,
    };
  }

  async refresh(document: HmiCanvasDocument): Promise<HmiRuntimeSnapshot> {
    const graph = this.compile(document);
    const queries = buildTelemetryQueries(document);
    const errors: HmiRuntimeError[] = [];
    const liveBindings = document.bindings.filter(
      (binding): binding is HmiLiveTelemetryBinding =>
        binding.source.kind === "telemetry" &&
        (binding.source.readMode ?? "live") === "live" &&
        Boolean(binding.source.channel?.trim()),
    );

    if (liveBindings.length > 0) {
      const now = new Date();
      const start = new Date(now.getTime() - 20 * 60 * 1000);
      const liveBindingsByDevice = new Map<string, typeof liveBindings>();

      for (const binding of liveBindings) {
        const deviceBindings = liveBindingsByDevice.get(binding.source.deviceId) ?? [];
        deviceBindings.push(binding);
        liveBindingsByDevice.set(binding.source.deviceId, deviceBindings);
      }

      await Promise.all(
        [...liveBindingsByDevice.entries()].map(async ([deviceId, bindings]) => {
          try {
            const rows = await getLiveTelemetry(
              deviceId,
              { start, end: now },
              this.scope.allWorkspaces ? "all" : null,
              this.scope.dashboardId ?? null,
            );
            const seriesEntries = Object.fromEntries(
              bindings.map((binding) => [
                sourceQueryId(binding.source),
                this.buildLiveSeries(rows, binding.source.channel?.trim() || ""),
              ]),
            );
            this.cache.setAll(seriesEntries);
          } catch (error) {
            const message = error instanceof Error ? error.message : "Could not load live telemetry.";
            const seriesEntries = Object.fromEntries(
              bindings.map((binding) => [
                sourceQueryId(binding.source),
                {
                  timestamps: [],
                  values: [],
                  bucket_seconds: 5,
                  error: message,
                } satisfies TelemetryBatchSeriesResultDto,
              ]),
            );
            this.cache.setAll(seriesEntries);
          }
        }),
      );
    }

    if (queries.length > 0) {
      const response = await postTelemetryBatchQuery({
        tenant_id: this.scope.allWorkspaces ? null : this.scope.tenantId,
        dashboard_id: this.scope.dashboardId ?? null,
        queries,
      }, {
        workspaceScope: this.scope.allWorkspaces ? "all" : null,
        dashboardId: this.scope.dashboardId ?? null,
      });
      this.cache.setAll(response.results);
    }

    for (const binding of document.bindings) {
      if (binding.source.kind !== "telemetry") {
        continue;
      }
      const key = sourceQueryId(binding.source);
      const series = this.cache.get(key);
      if (series?.error) {
        errors.push({
          elementId: binding.elementId,
          code: "telemetry_failed",
          message: series.error,
        });
      }
    }

    return {
      graph,
      telemetry: this.cache.toJSON(),
      errors,
    };
  }
}
